import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { Loader2, Phone, ShieldCheck, ArrowLeft, PhoneCall } from 'lucide-react';
import { api } from '../../context/AuthContext';

export function PhoneStep({ phone, setPhone, name, setName, sending, onSubmit }) {
  return (
    <div className="px-6 pb-6 space-y-4">
      <div className="space-y-2">
        <Label className="text-[13px]">Numéro de téléphone</Label>
        <div className="relative">
          <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+225 07 00 00 00 00"
            className="pl-9 h-11"
            inputMode="tel"
            autoFocus
          />
        </div>
        <p className="text-[11.5px] text-neutral-500">Format international recommandé. Si vous saisissez sans indicatif, +225 (Côte d'Ivoire) est ajouté.</p>
      </div>
      <div className="space-y-2">
        <Label className="text-[13px]">Votre prénom (facultatif)</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Awa, Kouadio, Fatou..." className="h-11" />
      </div>
      <Button disabled={sending} onClick={onSubmit} className="btn-anim w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[14px]">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
        Recevoir mon code
      </Button>
      <p className="text-[11px] text-neutral-500 text-center">En continuant, vous acceptez les conditions et la politique de confidentialité de fodindincagnotte.com.</p>
    </div>
  );
}

export function OtpStep({ normalizedPhone, otp, setOtp, verifying, verifyOtp, onBack, resendIn, onResend, sending, smsMock, devCode }) {
  const [voiceOk, setVoiceOk] = React.useState(false);
  const [voiceState, setVoiceState] = React.useState(null);

  React.useEffect(() => {
    api.get('/sms/status').then((r) => setVoiceOk(Boolean(r.data.voiceConfigured))).catch(() => {});
  }, []);

  const callVoice = async () => {
    setVoiceState('calling');
    try {
      await api.post('/auth/send-otp-voice', { phone: normalizedPhone });
      setVoiceState('done');
    } catch (e) {
      setVoiceState(e?.response?.data?.detail || "Échec de l'appel vocal. Réessayez.");
    }
  };

  const handleChange = (value) => {
    setOtp(value);
    if (value.length === 6) verifyOtp(value);
  };

  return (
    <div className="px-6 pb-6 space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[12.5px] text-neutral-600 hover:text-black">
        <ArrowLeft className="w-3.5 h-3.5" /> Changer de numéro
      </button>
      <div>
        <Label className="text-[13px]">Code reçu au {normalizedPhone}</Label>
        <div className="mt-2 flex justify-center">
          <InputOTP maxLength={6} value={otp} onChange={handleChange}>
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {smsMock ? (
          <p data-testid="otp-mode-hint" className="mt-3 text-[11.5px] text-neutral-500 text-center">Mode développement — code démo : <span className="font-semibold text-neutral-800">{devCode || '123456'}</span></p>
        ) : (
          <p data-testid="otp-mode-hint" className="mt-3 text-[11.5px] text-neutral-500 text-center">Un code à 6 chiffres vous a été envoyé par SMS. Il expire dans 10 minutes.</p>
        )}
      </div>
      <Button disabled={verifying || otp.length < 6} onClick={() => verifyOtp()} className="btn-anim w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white">
        {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Vérifier & se connecter
      </Button>
      <div className="text-center text-[12.5px]">
        {resendIn > 0 ? (
          <span className="text-neutral-500">Renvoyer le code dans {resendIn}s</span>
        ) : (
          <button className="text-[#d97757] hover:underline" onClick={onResend} disabled={sending}>Renvoyer le code</button>
        )}
      </div>
      {voiceOk && (
        <div className="text-center text-[12.5px]" data-testid="otp-voice-zone">
          {voiceState === 'calling' ? (
            <span className="text-neutral-500 inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Appel en cours...</span>
          ) : voiceState === 'done' ? (
            <span className="text-emerald-600 font-medium">📞 Vous allez recevoir un appel qui vous lira votre code</span>
          ) : (
            <>
              <button data-testid="otp-voice-call-btn" className="inline-flex items-center gap-1.5 text-neutral-600 hover:text-black underline underline-offset-2" onClick={callVoice} disabled={sending}>
                <PhoneCall className="w-3.5 h-3.5" /> SMS non reçu ? Recevoir le code par appel vocal
              </button>
              {voiceState && <p className="mt-1 text-red-600 text-[11.5px]">{voiceState}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
